import { createTheme, ThemeOptions } from '@mui/material/styles';
import { ColorMode } from '../context/ColorModeContext';

export const getCustomTheme = (mode: ColorMode) => {
  const isDark = mode === 'dark';

  const bankGothicFontFamily = [
    'BankGothic Md BT',
    'BankGothic MD BT',
    'Bank Gothic MD BT',
    'Bank Gothic',
    'Space Grotesk',
    '-apple-system',
    'sans-serif',
  ].join(',');

  const headingFontFamily = ['Space Grotesk', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'].join(',');

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      background: {
        default: isDark ? '#0b0f19' : '#f8fafc',
        paper: isDark ? '#161d2f' : '#ffffff',
      },
      primary: {
        main: isDark ? '#0066ff' : '#0050cb',
        dark: isDark ? '#0050cb' : '#003795',
        light: isDark ? '#40c2fd' : '#2b7fff',
        contrastText: '#ffffff',
      },
      secondary: {
        main: isDark ? '#00668a' : '#475569',
        light: '#7bd0ff',
      },
      text: {
        primary: isDark ? '#ffffff' : '#0f172a',
        secondary: isDark ? '#c2c6d8' : '#475569',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0',
    },
    typography: {
      fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'].join(','),
      h1: {
        fontFamily: bankGothicFontFamily,
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h2: {
        fontFamily: bankGothicFontFamily,
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontFamily: bankGothicFontFamily,
        fontWeight: 600,
      },
      h4: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
      },
      h5: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
      },
      h6: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
      },
      button: {
        fontFamily: ['Inter', 'sans-serif'].join(','),
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
            '@media (min-width: 600px)': {
              paddingLeft: '36px !important',
              paddingRight: '36px !important',
            },
            '@media (min-width: 900px)': {
              paddingLeft: '48px !important',
              paddingRight: '48px !important',
            },
            '@media (min-width: 1200px)': {
              paddingLeft: '64px !important',
              paddingRight: '64px !important',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '10px 24px',
            fontWeight: 600,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          containedPrimary: {
            backgroundColor: isDark ? '#0066ff' : '#0050cb',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: isDark ? '#0050cb' : '#003795',
              boxShadow: isDark
                ? '0 0 20px rgba(0, 102, 255, 0.4)'
                : '0 4px 14px rgba(0, 80, 203, 0.3)',
            },
          },
          outlinedPrimary: {
            borderColor: isDark ? 'rgba(0, 102, 255, 0.5)' : 'rgba(0, 80, 203, 0.5)',
            color: isDark ? '#ffffff' : '#0050cb',
            '&:hover': {
              borderColor: isDark ? '#0066ff' : '#0050cb',
              backgroundColor: isDark ? 'rgba(0, 102, 255, 0.08)' : 'rgba(0, 80, 203, 0.08)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#161d2f' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            borderRadius: 12,
            boxShadow: isDark ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

export const theme = getCustomTheme('dark');
